import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ImageSelectorPopover from 'components/Popovers/ImageSelectorPopover'; // Import the Popover component
import {
  APICreateMentorService,
  APIUpdateMentorServiceByID,
  APIDeleteMentorServiceByID
} from 'utils/api';
import { formatCentsToEuro, formatCurrency, formatPrice } from 'utils/utils';

const FormInput = ({ label, form, ...props }) => {
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.field.name === "price") {
      const value = e.target.value;
      if (value) {
        const floatValue = parseFloat(value.replace(",", "."));
        if (!isNaN(floatValue)) {
          form.setFieldValue(props.field.name, formatPrice(floatValue));
        }
      }
    }
    props.field.onBlur(e);
  };

  return (
    <div className="flex flex-col">
      <label htmlFor={props.id || props.name} className="mb-2 custom-text-[poppins-500-16px] text-[#111827]">
        {label}
      </label>
      <input
        type="text"
        className="w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white custom-text-[poppins-400-16px]"
        {...props.field}
        onBlur={handleBlur}
        placeholder={props.placeholder || ""}
      />
      <ErrorMessage name={props.field.name} component="div" className="mt-1 text-sm text-red-500" />
    </div>
  );
};

const FormTextarea = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label htmlFor={props.id || props.name} className="mb-2 custom-text-[poppins-500-16px] text-[#111827]">
      {label}
    </label>
    <textarea
      className="w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white resize-none custom-text-[poppins-400-16px]"
      rows={props.rows || 6}
      {...props.field}
      placeholder={props.placeholder || ""}
    />
    <ErrorMessage name={props.field.name} component="div" className="mt-1 text-sm text-red-500" />
  </div>
);

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  subtitle: Yup.string(),
  type: Yup.string(),
  audience: Yup.string(),
  duration: Yup.string().required('Duration is required').matches(/^(?:[1-9]|1[0-9]|2[0-4])$/, 'Duration must be between 1 and 24'),
  price: Yup.string().required("Price is required").test("is-at-least-1", `Price must be at least 1${formatCurrency('EUR')}`, (value) => {
    if (!value) return false;
    const floatValue = parseFloat(value.replace(",", "."));
    return !isNaN(floatValue) && floatValue >= 1;
  }),
  description: Yup.string().required('Description is required'),
  objective: Yup.string(),
  category: Yup.string().required('Category is required'),
  image: Yup.string(),
  cta_type: Yup.string(),
  cta_title: Yup.string(),
  cta_text: Yup.string(),
  cta_link: Yup.string().url('Invalid CTA URL'),
});

const FormSelect = ({ label, options, ...props }) => (
  <div className="flex flex-col">
    <label htmlFor={props.id || props.name} className="mb-2 custom-text-[poppins-500-16px] text-[#111827]">
      {label}
    </label>
    <select
      className="w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white custom-text-[poppins-400-16px]"
      {...props.field}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ErrorMessage name={props.field.name} component="div" className="mt-1 text-sm text-red-500" />
  </div>
);


const MentorshipCard = ({
  mentorId,
  service,
  onCancel,
}) => {
  // State for Popover anchor element and open status
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const popoverId = open ? 'image-selector-popover' : undefined;

  const queryClient = useQueryClient();
  const isNewService = !service?.id;

  const saveMutation = useMutation({
    mutationFn: (values: any) => {
      const payload = {
        ...values,
        price: Math.floor(parseFloat(values.price?.replace(",", ".")) * 100) || 0,
        duration: parseInt(values.duration?.split(" ")[0], 10) || 0,
      };

      if (service?.id) {
        return APIUpdateMentorServiceByID(mentorId, service.id, { ...payload, mentor_id: `${mentorId}` });
      } else {

        return APICreateMentorService(mentorId, { ...payload, mentor_id: `${mentorId}` });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentorServices', mentorId] });
      alert(isNewService ? "Service created!" : "Service updated!");
      if (isNewService && onCancel) onCancel();
    },
    onError: (error) => {
      alert(`Error saving service: ${error.message}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!service?.id) return Promise.reject("No service ID to delete");
      return APIDeleteMentorServiceByID(mentorId, service.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentorServices', mentorId] });
      alert("Service deleted!");
      if (onCancel) onCancel(service.id);
    },
    onError: (error) => {
      alert(`Error deleting service: ${error.message}`);
    }
  });

  const handleCancelClick = () => {
    if (isNewService && onCancel) {
      onCancel();
    } else if (service?.id) {
      deleteMutation.mutate();
    }
  };

  return (
    <div className="bg-white rounded-[15px] shadow-[0px_7px_29px_#64646f33] p-6 md:p-10 lg:p-[79px] w-full">
      <>
        <h3 className="mb-6 custom-text-[poppins-600-32px] text-[#141414]">
          Template of services to follow
        </h3>
        <img src={require("assets/mentor-template-of-services.png")} alt="Mentorship" className="mb-6 w-full h-auto rounded-[15px]" />
      </>
      <h3 className="mb-6 custom-text-[poppins-600-32px] text-[#141414]">
        {isNewService ? 'Add New Service' : 'Edit your service'}
      </h3>

      <Formik
        initialValues={{
          title: service?.title || '',
          subtitle: service?.subtitle || '',
          type: service?.type || '',
          audience: service?.audience || '',
          duration: service?.duration?.toString() || '',
          price: service?.price ? formatCentsToEuro(service.price) : '',
          description: service?.description || '',
          objective: service?.objective || '',
          category: service?.category || '',
          cta_type: service?.cta_type || 'url',
          cta_title: service?.cta_title || '',
          cta_text: service?.cta_text || '',
          cta_link: service?.cta_link || '',
          image: service?.image || '', // Add image initial value
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => saveMutation.mutate(values)}
        enableReinitialize
      >
        {({ isSubmitting, submitForm, values, setFieldValue }) => (
          <Form>


            <div className="grid grid-cols-1 gap-y-6 gap-x-8 mb-6 md:grid-cols-2">
              <Field name="title" component={FormInput} label="Title" placeholder="Consulting & Expertise" />
              <Field name="subtitle" component={FormInput} label="Tagline" placeholder="Boostez votre valeur..." />
              <Field name="type" component={FormInput} label="Type" placeholder="Accompagnement en groupe" />
              <Field name="audience" component={FormInput} label="For who ?" placeholder="Freelances & consultants" />
              <Field name="duration" component={FormInput} label="Duration (in months)" placeholder="3 month" />
              <Field name="price" component={FormInput} label={`Price (Euro ${formatCurrency('EUR')})`} placeholder={`300,99${formatCurrency('EUR')}`} />
              <Field name="category" component={FormInput} label="Category" placeholder="e.g., One-on-One Coaching" />
              <div className="mb-8">
                <label className="block mb-2 custom-text-[poppins-500-16px] text-[#111827]">
                  Service Image
                </label>
                <div className="flex items-center gap-4 p-4 border rounded-[15px] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.10)]">
                  <div className="flex overflow-hidden justify-center items-center w-24 h-24 bg-gray-100 rounded-lg">
                    {values.image ? (
                      values.image.startsWith('http') ? (
                        <img src={values.image} alt="Selected service" className="object-cover w-full h-full" />
                      ) : (
                        <img
                          src={require(`assets/${values.image}`)}
                          alt="Selected service"
                          className="object-cover w-full h-full"
                        />)
                    ) : (
                      <span className="text-xs text-gray-500">No Image</span>
                    )}
                  </div>
                <button
                  type="button"
                  aria-describedby={popoverId} // For accessibility
                  onClick={handlePopoverOpen} // Open popover on click
                  className="rounded-lg border border-[#407EC9] px-4 py-2 text-[#407EC9] transition-colors custom-text-[poppins-400-16px] hover:bg-blue-50"
                >
                  Change Image
                  </button>
                </div>
                <ErrorMessage name="image" component="div" className="mt-1 text-sm text-red-500" />
              </div>
            </div>
            <div className="mb-8">
              <Field name="description" component={FormTextarea} label="More details" placeholder="√ Sessions en groupe..." />
            </div>
            <div className="mb-8">
              <Field name="objective" component={FormInput} label="Objectif" placeholder="Gagnez en expertise..." />
            </div>

            <div className="pt-6 mt-6 mb-8 border-t">
              <h4 className="mb-4 custom-text-[poppins-500-18px] text-[#111827]">Call to Action (CTA)</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-8 md:grid-cols-2">
                <Field
                  name="cta_type"
                  component={FormSelect}
                  label="CTA Type"
                  options={[
                    { value: 'url', label: 'URL Link' },
                    { value: 'quick-charge', label: 'Quick Charge (Paid)' },
                  ]}
                />
                <Field name="cta_title" component={FormInput} label="CTA Title" placeholder="e.g., Book a Call" />
                {values.cta_type === 'url' && (
                  <>
                    <Field name="cta_text" component={FormInput} label="CTA Button Text" placeholder="e.g., Schedule Now" />
                    <Field name="cta_link" component={FormInput} label="CTA Link URL" placeholder="https://calendly.com/..." />
                  </>
                )}
                {values.cta_type === 'quick-charge' && (
                  <Field name="cta_text" component={FormInput} label="CTA Button Text" placeholder="e.g., Buy Now" />
                )}
              </div>
              <ErrorMessage name="image" component="div" className="mt-1 text-sm text-red-500" />
            </div>

            {/* Popover Rendering */}
            <ImageSelectorPopover
              id={popoverId}
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              currentImage={values.image}
              onSelect={(selectedImage) => {
                setFieldValue('image', selectedImage);
                handlePopoverClose(); // Close popover after selection
              }}
            />

            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={handleCancelClick}
                disabled={deleteMutation.isPending}
                className="rounded-lg border border-[#DC3545] px-[34px] py-[14px] text-[#DC3545] transition-colors custom-text-[poppins-400-20px] hover:bg-red-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : (isNewService ? 'Cancel' : 'Delete')}
              </button>
              <button
                type="button"
                onClick={submitForm}
                disabled={saveMutation.isPending}
                className="rounded-lg bg-[#407EC9] px-[34px] py-[14px] text-white transition-colors custom-text-[poppins-400-20px] hover:bg-[#3569A7] disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MentorshipCard;
